import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { GlobalVariables } from 'app/services/globals';
import { IDataflowBuildInTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';

const dataflowCassandra: IDataflowBuildInTypeMetadata = {
    type: DatasetFormatType.Cassandra,
    settings: adfModelTypeMap.cassandra.dataflowSettings,
    enableImportSchemaInSink: true
};
const connectionForm: ControlMetadata[] = [
    {
        type: 'textarea',
        bind: 'host',
        label: stringResources.ModelName_Host,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        rows: 2,
        description: stringResources.CassandraLinkedService_HostDescription
    },
    {
        type: 'textbox-number',
        bind: 'port',
        label: stringResources.ModelName_Port,
        isLabelVertical: true,
        inputType: 'number',
        min: 0,
        description: stringResources.CassandraLinkedService_PortDescription
    },
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        options: [
            {
                label: stringResources.Basic_DisplayText,
                value: 'Basic'
            },
            {
                label: stringResources.Anonymous_DisplayText,
                value: 'Anonymous'
            }
        ],
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        defaultValue: 'Basic',
        cases: [
            {
                caseValue: 'Basic',
                children: [
                    {
                        type: 'textbox',
                        bind: 'userName',
                        label: stringResources.ModelName_UserName,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        bind: 'password',
                        label: stringResources.ModelName_Password,
                        child: {
                            type: 'password',
                            label: stringResources.ModelName_Password,
                            required: true,
                            isLabelVertical: true
                        },
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Cassandra_DisplayText),
                            stringResources.ModelName_Password)
                    }
                ]
            }
        ]
    }
];

export const cassandraMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.cassandra.linkedServiceType,
    displayName: stringResources.Cassandra_DisplayText,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-Cassandra'
    },
    showWarningMessage: '{ version !== "v2" }',
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
        stringResources.VersionLabel.toLocaleLowerCase()
    ),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-cassandra',
    isFirstClass: true,
    category: ['NoSQL'],
    supportDatasetV2: true,
    copySourceTypes: [adfModelTypeMap.cassandra.copySourceType],
    supportDataFlowSource: () => GlobalVariables.enableCassandraDataFlow,
    dataflowSource: [dataflowCassandra],
    supportDataFlowSink: () => GlobalVariables.enableCassandraDataFlow,
    dataflowSink: [dataflowCassandra],
    tableColumnNameQuotes: ['"', '"'],
    fetchNestedNavTable: true,
    supportedInTrident: true,
    supportOPDG: GlobalVariables.enableOPDG,
    isDeprecated: GlobalVariables.showLegacy ? false : '{ version !== "v2" }',
    supportAutoFillCopilotInput: true,
    isKnownInPipelineContext: true,
    supportCopyJobSource: true,
    // Cassandra does not support multiple incremental columns.
    supportMultipleIncrementalColumns: false,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'Cassandra' as ResourceKind
    },
    timedQueryTemplate: 'select * from <<tableName>> \nwhere <<columnName>> >= \'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-dd HH:mm:ss\' )}\'' +
        ' \nAND <<columnName>> < \'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-dd HH:mm:ss\' )}\'',
    copySinkTypes: [],
    SHIRVersion: {
        v2: '5.50.0.0'
    },
    controls: [
            {
                type: 'radio-switch',
                isLabelVertical: true,
                label: stringResources.VersionLabel,
                required: true,
                bind: 'version',
                options: driverVersionVersionOptions,
                defaultValue: driverVersions.v2,
                hide: !GlobalVariables.showLegacy ? '{version === "v2" }' : false,
                cases: [
                    {
                        caseValue: driverVersions.v1,
                        children: [
                            {
                                type: 'hint',
                                hintMode: 'warning',
                                description: format(
                                    stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                    stringResources.VersionLabel.toLocaleLowerCase()),
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-cassandra'
                            },
                            ...connectionForm
                        ]
                    },
                    {
                        caseValue: driverVersions.v2,
                        children: [...connectionForm]
                    }
                ]
            },
            ...(connectionForm
                    .map((control) => ({
                        ...control,
                        hide: !GlobalVariables.showLegacy ? '{version !== "v2" }' : true
                    }))
                )
    ]
};
